/**
 * 保留トレイ一覧画面 (medium 信頼度のマージ候補)。
 * 仕様書: docs/01_spec/sales_implementation_prompt_v3_1.md §6.3
 *
 * v3.2.21: 商談ボードのタブからも埋め込めるよう Content/Page を分離。
 *  - `DealsInboxContent`: Screen ラッパなし。タブ等に埋め込む用
 *  - `DealsInboxPage`: Screen ラッパ付き。直リンク `/deals/inbox` 用 (後方互換)
 */
import { ChevronRight, Inbox } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { ConfidenceBadge } from '@/components/deals/ConfidenceBadge';
import { EmptyState } from '@/components/common/EmptyState';
import { Screen } from '@/components/layout/Screen';
import { Badge } from '@/components/ui/badge';
import { Spinner } from '@/components/ui/spinner';
import {
  useDealMergeCandidates,
  type DealMergeCandidateRecord,
} from '@/hooks/useDealMergeCandidates';
import { useMailContacts } from '@/hooks/useMailContacts';

function dateLabel(v: unknown): string {
  if (!v) return '';
  if (typeof v === 'string') {
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return '';
    return `${d.getMonth() + 1}/${d.getDate()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  }
  if (typeof v === 'object' && v !== null && 'toDate' in v) {
    const d = (v as { toDate: () => Date }).toDate();
    return `${d.getMonth() + 1}/${d.getDate()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  }
  return '';
}

/**
 * 保留トレイの中身のみ。Screen ラッパなしで、Tabs 内に埋め込み可能。
 */
export function DealsInboxContent() {
  const navigate = useNavigate();
  const { data, isLoading } = useDealMergeCandidates();
  // 全連絡先を取得 (limit を付けると枠外の連絡先名が解決できず顧客不明になる。
  // 内部は差分同期キャッシュで全件ロード済みのため追加 read は発生しない)
  const { data: contacts } = useMailContacts();

  const renderCard = (c: DealMergeCandidateRecord) => {
    const contact = contacts?.find((x) => x.id === c.contactId);
    return (
      <button
        key={c.id}
        type="button"
        onClick={() => navigate(`/deals/inbox/${c.id}`)}
        className="text-left rounded-md border border-[var(--border)] bg-[var(--bg-surface)] hover:border-[var(--accent)] transition-colors p-4 w-full"
      >
        <div className="flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className="text-sm font-medium">
                {contact?.name || c.contactId}
              </span>
              {contact?.defaultEmail && (
                <Badge variant="default">{contact.defaultEmail}</Badge>
              )}
            </div>
            <div className="text-xs text-[var(--text-muted)]">
              候補 {c.candidates.length} 件 · 作成 {dateLabel(c.createdAt)}
            </div>
          </div>
          <ConfidenceBadge confidence={c.bestConfidence} size="sm" />
          <ChevronRight className="size-4 text-[var(--text-muted)] self-center" />
        </div>
      </button>
    );
  };

  if (isLoading) return <Spinner />;
  if (!data || data.length === 0) {
    return (
      <EmptyState
        icon={Inbox}
        title="保留中の判断はありません"
        description="営業フォーム送信時に既存 Deal とのマージ判定が medium 信頼度になった場合、ここにケースが積まれます。"
      />
    );
  }
  return (
    <div className="flex flex-col gap-3">
      {data.map((c) => renderCard(c))}
    </div>
  );
}

export function DealsInboxPage() {
  return (
    <Screen
      title="保留トレイ"
      description="medium 信頼度 (0.40〜0.75) の判定を、根拠とともに人間に委ねる場所。"
    >
      <DealsInboxContent />
    </Screen>
  );
}
